import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    Modal,
    TextField,
    Button,
    Box,
    FormControlLabel,
    Checkbox,
    FormGroup,
    CircularProgress,
    FormControl,
    Autocomplete,
} from "@mui/material";
import BasicTitle from "./title";
import SwitchCustom from "./switch_custom";
import Api from "../libs/api";
import Storage from "../libs/storage";

const ModalCenter = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    max-width: 900px;
    background-color: #ffffff;
    padding: 40px;
    border-radius: 8px;
    border: 1px solid rgb(144, 144, 144);
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.75);
`;

const ModalContentScroll = styled.div`
    max-height: 70vh;
    overflow-y: auto;
    padding-right: 8px;
`;

const ModalClose = styled.img`
    position: absolute;
    right: 20px;
    top: 20px;
    cursor: pointer;
`;

const Section = styled.div`
    margin-bottom: 30px;
`;

const Label = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const LabelCheck = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: #333;
`;

const Counter = styled.div`
    font-size: 12px;
    text-align: right;
    font-weight: 500;
    span:first-child {
        color: ${({ $invalid }) => ($invalid ? "#d32f2f" : "#999")};
        font-weight: 500;
    }

    span:last-child {
        color: #000;
        font-weight: 500;
    }
`;

const BlockContainer = styled.div`
    display: flex;
    gap: 24px;
`;

const BlockBox = styled.div`
    flex: 1;
    background-color: #f6f6f6;
    padding: 24px;
    border-radius: 8px;
    margin-bottom: 10px;
`;

export default function ModalNewMessage({
    open,
    close,
    showErrorModal,
    onSave,
}) {
    const [platformApp, setPlatformApp] = useState(false);
    const [platformWeb, setPlatformWeb] = useState(false);
    const [highlight, setHighlight] = useState(false);
    const [blockEnabled, setBlockEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");

    const [sendToCompany, setSendToCompany] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState("");
    const [onlyOwner, setOnlyOwner] = useState(false);

    const [blocksApp, setBlocksApp] = useState({
        fullAccessApp: false,
        submitExpense: false,
        expenseHistory: false,
    });

    const [blocksWeb, setBlocksWeb] = useState({
        fullAccessWeb: false,
        expensesPage: false,
        exportExpenses: false,
        usersPage: false,
        creditsPage: false,
        settingsPage: false,
    });

    useEffect(() => {
        if (open) {
            setPlatformApp(false);
            setPlatformWeb(false);
            setHighlight(false);
            setBlockEnabled(false);
            setIsLoading(false);
            setTitle("");
            setMessage("");
            setBlocksApp({
                fullAccessApp: false,
                submitExpense: false,
                expenseHistory: false,
            });
            setBlocksWeb({
                fullAccessWeb: false,
                expensesPage: false,
                exportExpenses: false,
                usersPage: false,
                creditsPage: false,
                settingsPage: false,
            });
            setSendToCompany(false);
            setSelectedCompany("");
            setOnlyOwner(false);
        }
    }, [open]);

    const handleAppBlockChange = (field) => {
        setBlocksApp((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const handleWebBlockChange = (field) => {
        setBlocksWeb((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const isAnyPlatformSelected = platformApp || platformWeb;
    const isTitleValid = title.trim().length > 0 && title.length <= 256;
    const isMessageValid = message.trim().length > 0 && message.length <= 1000;

    const isAnyBlockSelected = Object.values({
        ...(platformApp ? blocksApp : {}),
        ...(platformWeb ? blocksWeb : {}),
    }).some((value) => value);

    const companyOk = !sendToCompany || !!selectedCompany;

    const canSubmit =
        isAnyPlatformSelected &&
        isTitleValid &&
        isMessageValid &&
        (!blockEnabled || isAnyBlockSelected) &&
        companyOk;

    const api = Api();
    const storage = Storage();

    const togglePlatformApp = () => {
        const newValue = !platformApp;
        setPlatformApp(newValue);
        if (!newValue) {
            setBlocksApp({
                fullAccessApp: false,
                submitExpense: false,
                expenseHistory: false,
            });
        }
    };

    const togglePlatformWeb = () => {
        const newValue = !platformWeb;
        setPlatformWeb(newValue);
        if (!newValue) {
            setBlocksWeb({
                fullAccessWeb: false,
                expensesPage: false,
                exportExpenses: false,
                usersPage: false,
                creditsPage: false,
                settingsPage: false,
            });
        }
    };

    const toggleBlockEnabled = () => {
        const newValue = !blockEnabled;
        setBlockEnabled(newValue);
        if (!newValue) {
            setBlocksApp({
                fullAccessApp: false,
                submitExpense: false,
                expenseHistory: false,
            });
            setBlocksWeb({
                fullAccessWeb: false,
                expensesPage: false,
                exportExpenses: false,
                usersPage: false,
                creditsPage: false,
                settingsPage: false,
            });
        }
    };

    const blockMap = {
        APP: {
            fullAccessApp: "all_access_app",
            submitExpense: "new_refund_app",
            expenseHistory: "history_refund_app",
        },
        WEB: {
            fullAccessWeb: "all_access_admin",
            expensesPage: "page_refunds_admin",
            exportExpenses: "export_admin",
            usersPage: "page_users_admin",
            creditsPage: "page_credits_admin",
            settingsPage: "settings_admin",
        },
    };

    const getBlockedAreas = () => {
        const blockedAreas = [];

        if (platformApp) {
            Object.entries(blocksApp).forEach(([key, value]) => {
                if (value) blockedAreas.push(blockMap.APP[key]);
            });
        }

        if (platformWeb) {
            Object.entries(blocksWeb).forEach(([key, value]) => {
                if (value) blockedAreas.push(blockMap.WEB[key]);
            });
        }

        return blockedAreas;
    };

    const handleCreateMessage = async () => {
        try {
            setIsLoading(true);
            const data = await storage.getItem("data");

            const payload = {
                title: title.trim(),
                message: message.trim(),
                platformWeb,
                platformApp,
                highlight,
                blockedAreas: getBlockedAreas(),
                companyId: sendToCompany ? selectedCompany : null,
                onlyOwner: onlyOwner ? onlyOwner : false,
            };

            const response = await api.createMessage(data.token, payload);
            const result = response?.data?.createMessage;

            if (result?.createMessageError?.messages?.length) {
                showErrorModal("Erro ao enviar mensagem.");
                setIsLoading(false);
                return;
            }

            close();
            onSave?.(result?.createMessageResult?.message);
            setIsLoading(false);
        } catch (err) {
            showErrorModal("Erro ao enviar mensagem.");
            setIsLoading(false);
        }
    };

    const [companies, setCompanies] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(false);

    const fetchCompanies = async () => {
        try {
            setCompaniesLoading(true);
            const data = await storage.getItem("data");
            const resp = await api.allCompanies(data.token);

            const edges =
                resp?.data?.allZagviewCompaniesWithOwners?.edges ?? [];
            const list = edges.map((edge) => edge?.node).filter(Boolean);

            setCompanies(list);
        } catch (e) {
            setCompanies([]);
        } finally {
            setCompaniesLoading(false);
        }
    };

    useEffect(() => {
        if (open && sendToCompany) {
            fetchCompanies();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sendToCompany]);

    const toggleOnlyOwner = () => {
        const v = !onlyOwner;
        setOnlyOwner(v);
        if (v) {
            setPlatformWeb(true);
            setPlatformApp(false);
            setBlocksApp({
                fullAccessApp: false,
                submitExpense: false,
                expenseHistory: false,
            });
        }
    };

    const toggleSendToCompany = () => {
        const v = !sendToCompany;
        setSendToCompany(v);
        if (!v) {
            setSelectedCompany("");
            setOnlyOwner(false);
        }
    };

    useEffect(() => {
        fetchCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Modal open={open} onClose={close}>
            <ModalCenter>
                <ModalClose
                    src="/img/modal_close.svg"
                    alt="Fechar"
                    onClick={close}
                />
                <BasicTitle title="NOVA MENSAGEM" margin={14} />
                <ModalContentScroll>
                    <Section style={{ marginTop: 8 }}>
                        <Label>
                            ENVIAR PARA EMPRESA ESPECÍFICA?
                            <SwitchCustom
                                checked={sendToCompany}
                                onChange={toggleSendToCompany}
                                size="small"
                            />
                        </Label>

                        {sendToCompany && (
                            <>
                                <FormControl
                                    fullWidth
                                    size="small"
                                    sx={{ mt: 1.5 }}
                                >
                                    <Autocomplete
                                        options={companies}
                                        sx={{ width: "50%" }}
                                        size="small"
                                        value={
                                            companies.find(
                                                (c) => c.id === selectedCompany
                                            ) || null
                                        }
                                        getOptionLabel={(company) =>
                                            company?.name ?? ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value.id
                                        }
                                        onChange={(_, newValue) => {
                                            setSelectedCompany(
                                                newValue
                                                    ? String(newValue.id)
                                                    : ""
                                            );
                                        }}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.id}>
                                                {option.name}
                                                {option.disabled && (
                                                    <p
                                                        style={{
                                                            marginLeft: 4,
                                                        }}
                                                    >
                                                        (
                                                        <span
                                                            style={{
                                                                textDecoration:
                                                                    "line-through",
                                                            }}
                                                        >
                                                            inativo
                                                        </span>
                                                        )
                                                    </p>
                                                )}
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="EMPRESA"
                                                placeholder="SELECIONE"
                                                variant="outlined"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        )}
                                        loading={companiesLoading}
                                        noOptionsText={
                                            companiesLoading
                                                ? "Carregando empresas..."
                                                : "Nenhuma empresa encontrada"
                                        }
                                    />
                                </FormControl>

                                <FormGroup sx={{ mt: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={onlyOwner}
                                                onChange={toggleOnlyOwner}
                                            />
                                        }
                                        label={
                                            <LabelCheck>
                                                ENVIAR APENAS PARA PROPRIETÁRIO
                                            </LabelCheck>
                                        }
                                    />
                                </FormGroup>
                            </>
                        )}
                    </Section>
                    {!onlyOwner && (
                        <Section>
                            <Label>PLATAFORMA</Label>
                            <FormGroup sx={{ gap: 0 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={platformApp}
                                            onChange={togglePlatformApp}
                                            size="small"
                                        />
                                    }
                                    label={<LabelCheck>APP CLIENTE</LabelCheck>}
                                    sx={{ mb: -1.5 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={platformWeb}
                                            onChange={togglePlatformWeb}
                                            size="small"
                                        />
                                    }
                                    label={<LabelCheck>WEB ADMIN</LabelCheck>}
                                />
                            </FormGroup>
                        </Section>
                    )}

                    <Section>
                        <Label>
                            EXIBIR COMO DESTAQUE?
                            <SwitchCustom
                                checked={highlight}
                                onChange={() => setHighlight(!highlight)}
                                size="small"
                            />
                        </Label>
                    </Section>

                    <Section>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Label>TÍTULO DA MENSAGEM</Label>
                            <Counter $invalid={title.length > 256}>
                                <span>{title.length}</span>/<span>256</span>
                            </Counter>
                        </div>
                        <TextField
                            fullWidth
                            size="small"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            error={title.length > 256}
                        />
                    </Section>

                    <Section>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Label>MENSAGEM</Label>
                            <Counter $invalid={message.length > 1000}>
                                <span>{message.length}</span>/<span>1000</span>
                            </Counter>
                        </div>
                        <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            error={message.length > 1000}
                        />
                    </Section>
                    <Section style={{ marginBottom: "20px" }}>
                        {" "}
                        <BasicTitle
                            title={"BLOQUEIO DE FUNCIONALIDADES"}
                        />{" "}
                        <Label>
                            {" "}
                            ATIVAR BLOQUEIO?{" "}
                            <SwitchCustom
                                checked={blockEnabled}
                                onChange={toggleBlockEnabled}
                                size="small"
                            />{" "}
                        </Label>{" "}
                    </Section>

                    {blockEnabled && (
                        <BlockContainer>
                            <BlockBox>
                                {platformApp ? (
                                    <>
                                        <Label style={{ marginBottom: "10px" }}>
                                            APP CLIENTE
                                        </Label>
                                        <FormGroup>
                                            <FormControlLabel
                                                style={{ marginBottom: "8px" }}
                                                control={
                                                    <SwitchCustom
                                                        size="small"
                                                        checked={
                                                            blocksApp.fullAccessApp
                                                        }
                                                        onChange={() =>
                                                            handleAppBlockChange(
                                                                "fullAccessApp"
                                                            )
                                                        }
                                                    />
                                                }
                                                label={
                                                    <LabelCheck>
                                                        ACESSO COMPLETO
                                                    </LabelCheck>
                                                }
                                            />

                                            <FormControlLabel
                                                style={{ marginBottom: "8px" }}
                                                control={
                                                    <SwitchCustom
                                                        size="small"
                                                        checked={
                                                            blocksApp.submitExpense
                                                        }
                                                        onChange={() =>
                                                            handleAppBlockChange(
                                                                "submitExpense"
                                                            )
                                                        }
                                                    />
                                                }
                                                label={
                                                    <LabelCheck>
                                                        SOLICITAR DESPESA
                                                    </LabelCheck>
                                                }
                                            />

                                            <FormControlLabel
                                                style={{ marginBottom: "8px" }}
                                                control={
                                                    <SwitchCustom
                                                        size="small"
                                                        checked={
                                                            blocksApp.expenseHistory
                                                        }
                                                        onChange={() =>
                                                            handleAppBlockChange(
                                                                "expenseHistory"
                                                            )
                                                        }
                                                    />
                                                }
                                                label={
                                                    <LabelCheck>
                                                        HISTÓRICO DE DESPESA
                                                    </LabelCheck>
                                                }
                                            />
                                        </FormGroup>
                                    </>
                                ) : (
                                    <div>
                                        <Label
                                            style={{
                                                marginBottom: "10px",
                                                color: "#999",
                                            }}
                                        >
                                            APP CLIENTE
                                        </Label>
                                        <LabelCheck
                                            style={{
                                                display: "block",
                                                marginBottom: 8,
                                                color: "#999",
                                            }}
                                        >
                                            ACESSO COMPLETO
                                        </LabelCheck>
                                        <LabelCheck
                                            style={{
                                                display: "block",
                                                marginBottom: 8,
                                                color: "#999",
                                            }}
                                        >
                                            SOLICITAR DESPESA
                                        </LabelCheck>
                                        <LabelCheck
                                            style={{
                                                display: "block",
                                                marginBottom: 0,
                                                color: "#999",
                                            }}
                                        >
                                            HISTÓRICO DE DESPESA
                                        </LabelCheck>
                                    </div>
                                )}
                            </BlockBox>
                            <BlockBox>
                                {platformWeb ? (
                                    <>
                                        <Label style={{ marginBottom: "10px" }}>
                                            WEB ADMIN
                                        </Label>
                                        <FormGroup>
                                            <FormControlLabel
                                                style={{ marginBottom: "8px" }}
                                                control={
                                                    <SwitchCustom
                                                        size="small"
                                                        checked={
                                                            blocksWeb.fullAccessWeb
                                                        }
                                                        onChange={() =>
                                                            handleWebBlockChange(
                                                                "fullAccessWeb"
                                                            )
                                                        }
                                                    />
                                                }
                                                label={
                                                    <LabelCheck>
                                                        ACESSO COMPLETO
                                                    </LabelCheck>
                                                }
                                            />

                                            <FormControlLabel
                                                style={{ marginBottom: "8px" }}
                                                control={
                                                    <SwitchCustom
                                                        size="small"
                                                        checked={
                                                            blocksWeb.expensesPage
                                                        }
                                                        onChange={() =>
                                                            handleWebBlockChange(
                                                                "expensesPage"
                                                            )
                                                        }
                                                    />
                                                }
                                                label={
                                                    <LabelCheck>
                                                        PÁGINA DE DESPESAS
                                                    </LabelCheck>
                                                }
                                            />

                                            <FormControlLabel
                                                style={{ marginBottom: "8px" }}
                                                control={
                                                    <SwitchCustom
                                                        size="small"
                                                        checked={
                                                            blocksWeb.exportExpenses
                                                        }
                                                        onChange={() =>
                                                            handleWebBlockChange(
                                                                "exportExpenses"
                                                            )
                                                        }
                                                    />
                                                }
                                                label={
                                                    <LabelCheck>
                                                        GERAR PDF/EXPORTAR
                                                        DESPESAS
                                                    </LabelCheck>
                                                }
                                            />

                                            <FormControlLabel
                                                style={{ marginBottom: "8px" }}
                                                control={
                                                    <SwitchCustom
                                                        size="small"
                                                        checked={
                                                            blocksWeb.usersPage
                                                        }
                                                        onChange={() =>
                                                            handleWebBlockChange(
                                                                "usersPage"
                                                            )
                                                        }
                                                    />
                                                }
                                                label={
                                                    <LabelCheck>
                                                        PÁGINA DE USUÁRIOS
                                                    </LabelCheck>
                                                }
                                            />

                                            <FormControlLabel
                                                style={{ marginBottom: "8px" }}
                                                control={
                                                    <SwitchCustom
                                                        size="small"
                                                        checked={
                                                            blocksWeb.creditsPage
                                                        }
                                                        onChange={() =>
                                                            handleWebBlockChange(
                                                                "creditsPage"
                                                            )
                                                        }
                                                    />
                                                }
                                                label={
                                                    <LabelCheck>
                                                        PÁGINA DE CRÉDITOS
                                                    </LabelCheck>
                                                }
                                            />

                                            <FormControlLabel
                                                style={{ marginBottom: "8px" }}
                                                control={
                                                    <SwitchCustom
                                                        size="small"
                                                        checked={
                                                            blocksWeb.settingsPage
                                                        }
                                                        onChange={() =>
                                                            handleWebBlockChange(
                                                                "settingsPage"
                                                            )
                                                        }
                                                    />
                                                }
                                                label={
                                                    <LabelCheck>
                                                        CONFIGURAÇÕES
                                                    </LabelCheck>
                                                }
                                            />
                                        </FormGroup>
                                    </>
                                ) : (
                                    <div>
                                        <Label
                                            style={{
                                                marginBottom: "10px",
                                                color: "#999",
                                            }}
                                        >
                                            WEB ADMIN
                                        </Label>
                                        <LabelCheck
                                            style={{
                                                display: "block",
                                                marginBottom: 8,
                                                color: "#999",
                                            }}
                                        >
                                            ACESSO COMPLETO
                                        </LabelCheck>
                                        <LabelCheck
                                            style={{
                                                display: "block",
                                                marginBottom: 8,
                                                color: "#999",
                                            }}
                                        >
                                            PÁGINA DE DESPESAS
                                        </LabelCheck>
                                        <LabelCheck
                                            style={{
                                                display: "block",
                                                marginBottom: 8,
                                                color: "#999",
                                            }}
                                        >
                                            GERAR PDF/EXPORTAR DESPESAS
                                        </LabelCheck>
                                        <LabelCheck
                                            style={{
                                                display: "block",
                                                marginBottom: 8,
                                                color: "#999",
                                            }}
                                        >
                                            PÁGINA DE USUÁRIOS
                                        </LabelCheck>
                                        <LabelCheck
                                            style={{
                                                display: "block",
                                                marginBottom: 8,
                                                color: "#999",
                                            }}
                                        >
                                            PÁGINA DE CRÉDITOS
                                        </LabelCheck>
                                        <LabelCheck
                                            style={{
                                                display: "block",
                                                marginBottom: 0,
                                                color: "#999",
                                            }}
                                        >
                                            CONFIGURAÇÕES
                                        </LabelCheck>
                                    </div>
                                )}
                            </BlockBox>
                        </BlockContainer>
                    )}
                </ModalContentScroll>
                <Box
                    display="flex"
                    justifyContent="flex-end"
                    gap={2}
                    marginTop={1}
                >
                    <Button variant="text" color="error" onClick={close}>
                        CANCELAR
                    </Button>
                    <Button
                        style={{ padding: "8px 32px" }}
                        variant="contained"
                        onClick={() => handleCreateMessage()}
                        disabled={!canSubmit || isLoading}
                    >
                        {isLoading ? (
                            <CircularProgress size={24} />
                        ) : (
                            "ENVIAR MENSAGEM"
                        )}
                    </Button>
                </Box>
            </ModalCenter>
        </Modal>
    );
}
